import "./UserRequest.css";
import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import moment from "moment";
import ManagerSubHeaderLinks from "../../components/managerSubHeaderLinks/ManagerSubHeaderLinks.jsx";
import DefaultProfileImage from "../../medias/images/default.jpg";
import UserShortProfile from "../../components/userShortProfile/UserShortProfile.jsx";
import LoadingSpinner from "../../components/loadSpinner/LoadingSpinner.jsx";
import {
  getAllUserRequests,
  requestUpdateManualTimeRequest,
  updateOvertimeRequestRequest,
} from "../../services/wisdomServices/requestService";
import { requestGetImagesFromBucket } from "../../services/wisdomServices/trackingService";
import { getUserInformations } from "../../services/wisdomServices/userService";
import { formatISOString } from "../../utils/helper/isoStringFormater";
import ManagerRequestsTable from "../../components/managerRequestsTable/ManagerRequestsTable.jsx";

/**
 * @function UserRequest
 * @returns {JSX.Element} - user requests management view
 */
function UserRequest() {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  const params = useParams();
  const { state } = useLocation();

  const employeeId = params.employeeId;

  const month = state?.month ?? currentMonth;
  const year = state?.year ?? currentYear;

  const [selectedMonth, setSelectedMonth] = useState(month);
  const [selectedYear, setSelectedYear] = useState(year);

  const [data, setData] = useState([]);
  const [euser, setEuser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);

  const requestStatus = ["Requested", "Approved", "Rejected"];

  /**
   * @async
   * @function listRequests
   * @description - fetches and formats all user requests
   */
  const listRequests = async (monthParam, yearParam) => {
    setLoadingRequests(true);

    const monthRequested = monthParam ?? selectedMonth;
    const yearRequested = yearParam ?? selectedYear;

    setSelectedMonth(monthRequested);
    setSelectedYear(yearRequested);

    try {
      const requestParams = {
        month: monthRequested,
        year: yearRequested,
      };

      const rsummary = await getAllUserRequests(employeeId, requestParams);

      const requests = rsummary.map((item) => {
        const endTimeWithExtraMinutes = item.endTime
          ? moment.utc(item.endTime, "HH:mm").format("HH:mm")
          : "";

        return {
          id: item.id,
          date: formatISOString(item.date),
          type: item.type,
          hours: item.hours,
          manager: item.managerName,
          projectId: item.projectId,
          project: item.projectName,
          reason: item.reason,
          status: requestStatus[item.statusCode],
          approvalDate:
            item.approvalDate == null ? "" : formatISOString(item.approvalDate),
          startTime: item.startTime,
          endTime: endTimeWithExtraMinutes,
          rejectReasonId: item.rejectReasonId,
          rejectReason: item.rejectReason,
        };
      });

      setData(requests);
    } catch (error) {
      console.error(error);
      setData([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  /**
   * @async
   * @function getUser
   * @description - fetches user profile information
   */
  const getUser = async () => {
    setLoading(true);

    try {
      const u = await getUserInformations(employeeId);

      if (!u.image) {
        u.image = DefaultProfileImage;
      } else {
        const blob = await requestGetImagesFromBucket(
          u.id,
          u.image,
          "profile"
        );

        u.image = URL.createObjectURL(blob);
      }

      setEuser(u);
    } catch (error) {
      console.error(error);
      setEuser(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * @async
   * @function changeStatus
   * @param {number} requestId
   * @param {string} requestType
   * @param {number} requestStatus
   * @param {number} reasonId
   * @description - changes request requestStatus based on request type
   */
  const changeStatus = async (requestId, requestType, requestStatus, reasonId) => {
    try {
      const keys = {
        status: requestStatus,
      };

      if (reasonId) {
        keys.reasonId = reasonId;
      }

      if (requestType === "manualTime") {
        await requestUpdateManualTimeRequest(employeeId, requestId, keys);
      } else {
        await updateOvertimeRequestRequest(employeeId, requestId, keys);
      }

      await listRequests(selectedMonth, selectedYear);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!employeeId) return;

    setSelectedMonth(month);
    setSelectedYear(year);

    getUser();
    listRequests(month, year);
  }, [employeeId]);

  return (
    <div className="manager-view-user-requests">
      {loading && <LoadingSpinner />}

      {!loading && euser && (
        <div className="manager-view-user-requests-main">
          <div className="my-dashboard-header">
            <UserShortProfile
              employeeName={euser.firstName + " " + euser.lastName}
              employeePronoun={euser.pronoun}
              employeeDesignation={euser.jobDescription}
              employeeID={euser.id}
              employeeUsername={euser.username}
              employeeLocation={euser.city}
              employeePicture={euser.image}
              employeeDisabled={euser.isDisabled}
              employeeContractEndDate={euser.contractEndDate}
              employeeDepartment={euser.department}
              employeeIdvStatus={euser.trolleyIdvStatus}
              employeePersonalEmail={euser.personalEmail}
              showManagerLinks={true}
            />

          </div>

          <div className="users-request-summaryContent">
            <div className="manual-hours ot-hours">
              <ManagerRequestsTable
                key={employeeId}
                defaultMonth={selectedMonth}
                defaultYear={selectedYear}
                data={data}
                onDateSelect={listRequests}
                onSubmitted={changeStatus}
                loading={loadingRequests}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserRequest;